import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  PdfEditorStore,
  SOFTLINE_FEATURE_PDF_EDITOR } from '@softapps/allgemein/pdf-editor';
import {
  FreigabeStore,
  SOFTLINE_CONST_WORKFLOW_DATA_DETAIL,
  SOFTLINE_FEATURE_FREIGABE,
  SOFTLINE_MESSAGE_SOFTAPPS_FIBU_FREIGABE_NEXT,
  SOFTLINE_MESSAGE_SOFTAPPS_FIBU_FREIGABE_REQUEST_NEXT,
  Workflow,
  WorkflowData } from '@softapps/fibu/freigabe';
import {
  ArchiveFile,
  ArchiveStore,
  Command,
  CommandStore,
  showRequestErrors,
  SOFTLINE_FEATURE_ARCHIVE,
  SOFTLINE_FEATURE_COMMANDS } from '@softline/application';
import {
  ApiResourceLocation,
  asType,
  base64Decode,
  Mediator,
  ResourceService,
  SOFTLINE_SERVICE_HTTP,
  Store } from '@softline/core';
import {
  MessageBarStore,
  SOFTLINE_DATA_CONTEXT,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators } from '@softline/ui-core';
import {
  BehaviorSubject,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  skip,
  startWith,
  Subscription } from 'rxjs';
import { AbauWorkflowData } from '../../types/abau-workflow-data';

const SOFTLINE_API_RECALCULATE = '/v1/workflow/entities/{{id}}/data/recalculate';

import { DateInputComponent } from '@softline/ui-core/date-input';
import { FileInputComponent } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { InterpolatePipe } from '@softline/ui-core';
import { MultilineInputComponent } from '@softline/ui-core';
import { NumberInputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { ValidationContainerComponent } from '@softline/ui-core';
import { ValidationMessagePipe } from '@softline/ui-core';
@Component({
  selector: 'softline-abau-freigabe',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DateInputComponent,
    FileInputComponent,
    FormatPipe,
    InterpolatePipe,
    MultilineInputComponent,
    NumberInputComponent,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe,
  ],
  templateUrl: './abau-freigabe.widget.html',
  styleUrls: ['./abau-freigabe.widget.css'],
  changeDetection: ChangeDetectionStrategy.OnPush })
export class AbauFreigabeWidget implements OnInit, OnDestroy {
  private formSubscription?: Subscription;
  private workflowSubscription?: Subscription;
  private mediatorSubscription?: Subscription;

  private destroyRef = inject(DestroyRef);
  private submitting = false;

  form = new FormGroup({
    id: new FormControl(undefined),
    faelligkeitMitglied: new FormControl(undefined),
    faelligkeitLieferant: new FormControl(undefined),
    wfprocessid: new FormControl(undefined),
    abstrichNetto: new FormControl(undefined, [Validators.min(0)]),
    ruecklassProzent: new FormControl(undefined, [Validators.min(0)]),
    ruecklassWert: new FormControl(undefined),
    skontoProzKu: new FormControl(undefined, [Validators.min(0)]),
    skontoWertKu: new FormControl(undefined),
    begruendung: new FormControl(undefined, {
      updateOn: 'blur',
      validators: [Validators.min(0)] }),
    files: new FormControl<File[] | null>(null) });

  saving$ = new BehaviorSubject<boolean>(false);
  formState$ = this.form.statusChanges.pipe(startWith(undefined));

  workflow$ = this.store
    .observe(
      SOFTLINE_FEATURE_FREIGABE,
      FreigabeStore.getters.entityWithDetails,
      this.context
    )
    .pipe(asType<Workflow & { detail: AbauWorkflowData }>());

  faelligkeitFeldAenderung?: string = undefined;

  constructor(
    private store: Store,
    @Inject(SOFTLINE_DATA_CONTEXT) private context: number,
    private mediator: Mediator,
    @Inject(SOFTLINE_SERVICE_HTTP)
    private httpService: ResourceService<ApiResourceLocation>
  ) {}

  async ngOnInit(): Promise<void> {
    this.form.controls.faelligkeitMitglied.valueChanges
      .pipe(
        startWith(this.form.controls.faelligkeitMitglied.value),
        distinctUntilChanged()
      )
      .pipe(
        combineLatestWith(
          this.form.controls.faelligkeitLieferant.valueChanges.pipe(
            startWith(this.form.controls.faelligkeitLieferant.value),
            distinctUntilChanged()
          ),
          this.form.controls.abstrichNetto.valueChanges.pipe(
            startWith(this.form.controls.abstrichNetto.value),
            distinctUntilChanged()
          ),
          this.form.controls.ruecklassProzent.valueChanges.pipe(
            startWith(this.form.controls.ruecklassProzent.value),
            distinctUntilChanged()
          ),
          this.form.controls.skontoProzKu.valueChanges.pipe(
            startWith(this.form.controls.skontoProzKu.value),
            distinctUntilChanged()
          ),
          this.form.controls.begruendung.valueChanges.pipe(
            startWith(this.form.controls.begruendung.value),
            distinctUntilChanged()
          )
        ),
        skip(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((values) => {
        console.log('value changes: ', values);
        const value = {
          ...this.form.value,
          faelligkeitMitglied: values[0],
          faelligkeitLieferant: values[1],
          abstrichNetto: values[2],
          ruecklassProzent: values[3],
          skontoProzKu: values[4],
          begruendung: values[5] };
        if (this.submitting) return;
        this.recalculate(value as any);
      });

    const workflow = await firstValueFrom(
      this.workflow$.pipe(filter((o) => !!o && !!o?.detail))
    );
    this.form.patchValue(workflow?.detail as any, { emitEvent: false, onlySelf: true });

    this.mediatorSubscription = this.mediator.subscribe(async (message) => {
      switch (message.name) {
        case SOFTLINE_MESSAGE_SOFTAPPS_FIBU_FREIGABE_NEXT:
          await this.save();
          break;
        default:
          break;
      }
    });
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: AbauFreigabeWidget,
      commands: this.createCommands() });
  }

  ngOnDestroy(): void {
    if (this.formSubscription && !this.formSubscription.closed)
      this.formSubscription.unsubscribe();
    this.formSubscription = undefined;
    if (this.mediatorSubscription && !this.mediatorSubscription.closed)
      this.mediatorSubscription.unsubscribe();
    this.mediatorSubscription = undefined;
    if (this.workflowSubscription && !this.workflowSubscription.closed)
      this.workflowSubscription.unsubscribe();
    this.workflowSubscription = undefined;
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      AbauFreigabeWidget
    );
  }

  createCommands(): Command[] {
    return [
      {
        icon: 'fas fa-save',
        name: '#FREIGABE.WIDGET.ABAU_FREIGABE.SAVE',
        class: 'menu action-menu action-menu-top',
        canExecute: this.saving$.pipe(
          combineLatestWith(this.formState$),
          map(([o, state]) => !o && state !== 'INVALID')
        ),
        execute: () => this.save() }];
  }

  private async recalculate(
    value: Partial<{
      abstrichNetto: number;
      ruecklassProzent: number;
      skontoProzKu: number;
      wfprocessid: string;
      faelligkeitMitglied: string;
      faelligkeitLieferant: string;
    }>
  ): Promise<void> {
    const workflow = await firstValueFrom(this.workflow$);
    if (
      workflow.detail.abstrichNetto === value.abstrichNetto &&
      workflow.detail.ruecklassProzent === value.ruecklassProzent &&
      workflow.detail.skontoProzKu === value.skontoProzKu &&
      workflow.detail.faelligkeitMitglied === value.faelligkeitMitglied &&
      workflow.detail.faelligkeitLieferant === value.faelligkeitLieferant
    ) {
      this.store.commit(SOFTLINE_FEATURE_FREIGABE, FreigabeStore.mutations.patchDetail, {
        name: SOFTLINE_CONST_WORKFLOW_DATA_DETAIL,
        id: workflow.detail.wfprocessid,
        value: value });
      return;
    }

    try {
      this.form.disable({ emitEvent: false });
      const result = await lastValueFrom(
        this.httpService.create<any, any>(
          {
            path: SOFTLINE_API_RECALCULATE,
            pathParams: { id: workflow.id } },
          {
            ...workflow.detail,
            ...value,
            faelligkeitFeldAenderung: this.faelligkeitFeldAenderung }
        )
      );

      // this.form.patchValue(result, { emitEvent: false });
      this.store.commit(SOFTLINE_FEATURE_FREIGABE, FreigabeStore.mutations.updateDetail, {
        name: SOFTLINE_CONST_WORKFLOW_DATA_DETAIL,
        id: result.wfprocessid,
        value: result });

      console.log('result: ', result);
      this.form.patchValue(
        {
          abstrichNetto: result.abstrichNetto,
          ruecklassProzent: result.ruecklassProzent,
          skontoProzKu: result.skontoProzKu,
          skontoWertKu: result.skontoWertKu,
          begruendung: result.begruendung,
          faelligkeitMitglied: result.faelligkeitMitglied,
          faelligkeitLieferant: result.faelligkeitLieferant,
          files: result.files },
        { emitEvent: false, onlySelf: true }
      );

      this.form.enable({ emitEvent: false });
    } catch (e) {
      this.submitting = false;
      this.form.enable({ emitEvent: false });
      showRequestErrors(this.store, e);
    }
  }

  async onSubmit(): Promise<void> {
    try {
      this.submitting = true;
      await this.save();
      await this.mediator.next({
        name: SOFTLINE_MESSAGE_SOFTAPPS_FIBU_FREIGABE_REQUEST_NEXT });
    } catch (e) {
      showRequestErrors(this.store, e);
      this.submitting = false;
    }
  }

  async save(): Promise<void> {
    const workflow = await firstValueFrom(this.workflow$);
    const { files, ...value } = this.form.value;
    if (!workflow.detail.wfprocessid)
      throw new Error('[freigabe.component] onSubmit:  No wfprocessid');
    try {
      this.saving$.next(true);
      await this.uploadDocument(workflow);

      if (files && files?.length > 0 && workflow?.detail?.archiveKey) {
        await this.store.dispatch(SOFTLINE_FEATURE_ARCHIVE, ArchiveStore.actions.upload, {
          archiveKey: workflow?.detail?.archiveKey,
          files: files || [] });
      }

      await this.store.dispatch(
        SOFTLINE_FEATURE_FREIGABE,
        FreigabeStore.actions.patchDetail,
        {
          name: SOFTLINE_CONST_WORKFLOW_DATA_DETAIL,
          patch: { id: workflow.detail.wfprocessid, changes: value as any } }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#FREIGABE.MESSAGES.SUCCESS.SAVE'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    } finally {
      this.saving$.next(false);
    }
  }

  private async uploadDocument(
    workflow: Workflow & { detail: WorkflowData }
  ): Promise<void> {
    const editedDocument = this.store.get(
      SOFTLINE_FEATURE_PDF_EDITOR,
      PdfEditorStore.getters.document,
      workflow.detail.fileKey
    ) as any;
    if (
      !workflow.detail.fileKey ||
      !workflow.detail.archiveKey ||
      !editedDocument?.edited
    )
      return;

    const fileName = (JSON.parse(base64Decode(workflow.detail.fileKey)) as ArchiveFile)
      .metaData.name;
    const file = new File([editedDocument.data], fileName);
    await this.store.dispatch(SOFTLINE_FEATURE_ARCHIVE, ArchiveStore.actions.upload, {
      archiveKey: workflow.detail.archiveKey,
      files: [file] });
    await this.store.commit(
      SOFTLINE_FEATURE_PDF_EDITOR,
      PdfEditorStore.mutations.remove,
      workflow.detail.fileKey
    );
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      '#FREIGABE.MESSAGES.SUCCESS.UPLOAD'
    );
  }
}
